import { AdminScorecards } from '../components/AdminScorecards';
import { RevealControls } from '../components/RevealControls';
import { ScoringProgress } from '../components/ScoringProgress';
import { ScoreSegment as Seg } from '../components/ScoreSegment';
import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  can,
  canTransitionPhase,
  computeTotal,
  maxTotal,
  DEFAULT_SCORING_SCHEME,
  type EventPhase,
  type LiveTastingView,
  type LiveWineView,
} from '@gwc/shared';
import { tastingsApi, type Registration, type ScoreBody } from '../api/tastings';
import { membersApi } from '../api/members';
import { useAuth } from '../auth/AuthProvider';
import { ordinal, PHASE_LABEL, points } from '../lib/format';
import { CellarWinePicker } from '../components/CellarWinePicker';
import { PageSkeleton } from '../components/Skeleton';
import { useConfirm } from '../components/ui';
import { usePageVisible } from '../hooks/useDebouncedValue';

type Components = { sightNose: number | null; taste: number | null; overall: number | null };

function clientIdFor(eventId: string, wineId: string): string {
  const key = `gwc:cid:${eventId}:${wineId}`;
  let v = localStorage.getItem(key);
  if (!v) {
    v = crypto.randomUUID();
    localStorage.setItem(key, v);
  }
  return v;
}

function pollInterval(phase: string, visible: boolean): number | false {
  if (!visible) return false;
  if (phase === 'final' || phase === 'setup') return 10_000;
  return 2000;
}

/** Best rank first; wines without a rank (no scores, or not revealed yet) last. */
function byRank(a: LiveWineView, b: LiveWineView): number {
  return (a.groupRank ?? 99) - (b.groupRank ?? 99) || a.letter.localeCompare(b.letter);
}

/** Ranks held by more than one wine. Ranking is "1224" style, so a tie shares a rank. */
function tiedRanks(wines: LiveWineView[]): Set<number> {
  const seen = new Set<number>();
  const tied = new Set<number>();
  for (const w of wines) {
    if (w.groupRank == null) continue;
    if (seen.has(w.groupRank)) tied.add(w.groupRank);
    seen.add(w.groupRank);
  }
  return tied;
}

/** "7th", or "tied 7th" when another wine shares the rank. */
function rankLabel(rank: number, tied: Set<number>): string {
  return `${tied.has(rank) ? 'tied ' : ''}${ordinal(rank)}`;
}

export function LiveTasting() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const pageVisible = usePageVisible();
  const adminRole = user ? can(user.roles, 'tasting:run') : false;
  const canScore = user ? can(user.roles, 'tasting:score') : false;

  const {
    data: view,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['live', id],
    queryFn: async () => {
      const prev = qc.getQueryData<LiveTastingView>(['live', id]);
      const next = await tastingsApi.live(id, prev?.version);
      if (next === null && prev) return prev;
      if (!next) throw new Error('Live view unavailable');
      return next;
    },
    refetchInterval: (q) => pollInterval(q.state.data?.phase ?? 'setup', pageVisible),
  });

  if (isLoading) return <PageSkeleton lines={4} />;
  if (isError || !view) {
    return <p className="text-wine-300">Could not load this tasting. Try refreshing the page.</p>;
  }

  const canControl = adminRole && view.canManage === true;
  // During the reveal a member only receives results for wines already unveiled,
  // and the reveal climbs from the bottom of the table — so the list grows as
  // each wine is revealed, with the best of those so far on top.
  const standings = view.wines
    .filter((w) => canControl || view.phase !== 'revealing' || w.revealed || w.groupRank != null)
    .sort(byRank);

  const showScorecard =
    canScore && ['scoring_open', 'scoring_closed', 'revealing', 'final'].includes(view.phase);
  // Once the reveal starts it's the admin's main job, so it moves up above the
  // scorecards instead of sitting at the bottom of a long page. It moves by CSS
  // order, not by place in the tree: remounting it would drop whatever an admin
  // has typed into a row but not saved yet.
  const revealFirst = view.phase === 'revealing' || view.phase === 'final';

  return (
    <div className="flex flex-col gap-8">
      <div>
        <Link to="/tastings" className="text-xs text-cream-300/60 hover:text-gold-200">
          ← All tastings
        </Link>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <h1 className="font-display text-3xl text-cream-50">{view.title}</h1>
          <span className="rounded-full border border-gold-600/40 px-3 py-1 text-xs uppercase tracking-wide text-gold-200">
            {PHASE_LABEL[view.phase] ?? view.phase}
          </span>
        </div>
        {view.theme && <p className="text-sm text-cream-300/60">{view.theme}</p>}
      </div>

      {canControl && <PhaseControls id={id} view={view} />}

      {canControl && view.scoringProgress && <ScoringProgress rows={view.scoringProgress} />}

      {canControl || view.phase === 'revealing' || view.phase === 'final' ? (
        <Standings
          wines={standings}
          phase={view.phase}
          revealedCount={view.wines.filter((w) => w.revealed).length}
          totalCount={view.wines.length}
          showMemberRank={canScore}
        />
      ) : (
        <p className="text-sm text-cream-300/70">
          Score each wine below. Group standings appear when the reveal begins.
        </p>
      )}

      {(showScorecard || canControl) && (
        <div className="order-2 space-y-8">
          {showScorecard && <ScoringGrid view={view} readOnly={view.phase !== 'scoring_open'} />}
          {canControl && <AdminScorecards eventId={id} />}
        </div>
      )}

      {canControl && (
        <div className={revealFirst ? 'order-1' : 'order-3'}>
          <AdminReveal
            view={view}
            onChange={() => qc.invalidateQueries({ queryKey: ['live', id] })}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Whether a step button is live. The shared phase machine is the rule; a
 * finalized tasting only goes back through its own "Reopen tasting" button, so
 * "Start reveal" doesn't light up again once the machine allows final → revealing.
 * A reopened tasting already sent its results, so it can't go back to scoring.
 */
function canStep(view: LiveTastingView, to: EventPhase): boolean {
  const from = view.phase;
  if (view.reopened && to === 'scoring_closed') return false;
  return from !== 'final' && from !== to && canTransitionPhase(from, to);
}

/** What the confirmation says before a phase change, given where it's coming from. */
function phaseConfirm(
  from: EventPhase,
  to: EventPhase,
): { title: string; description?: string; confirmLabel?: string } {
  switch (to) {
    case 'scoring_open':
      return from === 'scoring_closed'
        ? {
            title: 'Reopen scoring?',
            description: 'Members can add and change their scores again until you close scoring.',
            confirmLabel: 'Reopen scoring',
          }
        : {
            title: 'Open scoring?',
            description:
              'Members can start scoring every wine, and everyone attending is told the tasting is live.',
            confirmLabel: 'Open scoring',
          };
    case 'scoring_closed':
      return from === 'revealing'
        ? {
            title: 'Pause the reveal?',
            description:
              'The tasting goes back to scoring closed and members stop seeing the standings. Wines already revealed are hidden again until you reveal them.',
            confirmLabel: 'Pause reveal',
          }
        : {
            title: 'Close scoring?',
            description:
              'Members can no longer add or change scores. You can reopen scoring if someone missed a wine.',
            confirmLabel: 'Close scoring',
          };
    case 'revealing':
      return from === 'final'
        ? {
            title: 'Reopen this tasting?',
            description:
              'The tasting goes back to the reveal so you can correct a wine or who brought it. Scoring stays closed. Finalize again when you’re done; the results aren’t sent a second time.',
            confirmLabel: 'Reopen tasting',
          }
        : {
            title: 'Start the reveal?',
            description:
              'Members see each wine’s rank and score as you reveal it. Reveal from the bottom of the table up.',
            confirmLabel: 'Start reveal',
          };
    case 'final':
      return {
        title: 'Finalize this tasting?',
        description:
          'The results are locked in and sent to everyone who scored, and every wine is revealed to the club.',
        confirmLabel: 'Finalize and send results',
      };
    default:
      return { title: `Move to ${PHASE_LABEL[to] ?? to}?` };
  }
}

function PhaseControls({ id, view }: { id: string; view: LiveTastingView }) {
  const { phase } = view;
  const qc = useQueryClient();
  const confirm = useConfirm();
  const mut = useMutation({
    mutationFn: (p: EventPhase) => tastingsApi.setPhase(id, p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['live', id] }),
  });
  const steps: [EventPhase, string][] = [
    ['scoring_open', 'Open scoring'],
    ['scoring_closed', 'Close scoring'],
    ['revealing', 'Start reveal'],
    ['final', 'Finalize'],
  ];

  async function move(to: EventPhase) {
    if (await confirm(phaseConfirm(phase, to))) mut.mutate(to);
  }

  return (
    <div className="card">
      <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">Tasting control</h2>
      <div className="flex flex-wrap gap-2">
        {steps.map(([p, label]) => (
          <button
            key={p}
            type="button"
            onClick={() => void move(p)}
            disabled={!canStep(view, p) || mut.isPending}
            aria-current={phase === p ? 'step' : undefined}
            className={`btn px-3 py-1.5 text-xs ${
              phase === p
                ? 'bg-gold-600 text-on-gold disabled:opacity-100'
                : 'border border-gold-600/40 text-cream-100 hover:border-gold-400 disabled:hover:border-gold-600/40'
            }`}
          >
            {label}
          </button>
        ))}
        {/* Imported and history tastings are corrected in place, not reopened. */}
        {phase === 'final' && view.canReopen && (
          <button
            type="button"
            onClick={() => void move('revealing')}
            disabled={mut.isPending}
            className="btn border border-wine-500/60 px-3 py-1.5 text-xs text-cream-100 hover:border-wine-500 hover:text-gold-200"
          >
            Reopen tasting
          </button>
        )}
      </div>
    </div>
  );
}

function Standings({
  wines,
  phase,
  revealedCount,
  totalCount,
  showMemberRank,
}: {
  wines: LiveWineView[];
  phase: string;
  revealedCount: number;
  totalCount: number;
  showMemberRank: boolean;
}) {
  const tied = tiedRanks(wines);
  return (
    <div className="card">
      <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">
        Live standings {phase === 'final' && '· Final'}
        {phase === 'revealing' && `· ${revealedCount} of ${totalCount} revealed`}
      </h2>
      {phase === 'revealing' && wines.length === 0 && (
        <p className="text-sm text-cream-300/70">
          The reveal is about to begin. Wines appear here as they’re revealed, from the bottom of
          the table up.
        </p>
      )}
      <div className="space-y-1">
        {wines.map((w) => (
          <div
            key={w.id}
            className="flex flex-wrap items-center gap-3 border-b border-ink-800/60 py-2 last:border-0"
          >
            <span className="flex w-8 flex-col items-center font-display text-lg leading-tight text-gold-300">
              {w.groupRank ?? '—'}
              {w.groupRank != null && tied.has(w.groupRank) && (
                <span className="font-sans text-[10px] uppercase tracking-wide text-cream-300/60">
                  tied
                </span>
              )}
            </span>
            <span className="flex h-8 w-8 items-center justify-center rounded bg-ink-950 font-display text-cream-50">
              {w.letter}
            </span>
            <div className="min-w-[140px] flex-1">
              {w.revealed && w.wineName ? (
                w.catalogId ? (
                  <Link to={`/cellar/${w.catalogId}`} className="text-cream-50 hover:text-gold-200">
                    {w.wineName}
                    {w.vintage ? ` ’${String(w.vintage).slice(-2)}` : ''}
                  </Link>
                ) : (
                  <span className="text-cream-50">
                    {w.wineName}
                    {w.vintage ? ` ’${String(w.vintage).slice(-2)}` : ''}
                  </span>
                )
              ) : (
                <span className="text-cream-300/40">
                  {w.revealed ? `Wine ${w.letter} · name not entered yet` : 'Hidden until revealed'}
                </span>
              )}
              {/* A structured RSVP files the wine under its commune or vineyard
                  ("Meursault"), so the producer goes beside it, as on the app. */}
              {w.revealed && w.wineName && w.producer && (
                <span className="block text-xs text-cream-300/70">{w.producer}</span>
              )}
              {/* The host's screen may be up on the wall: even for admins the name
                  waits for "who brought this?". */}
              {w.revealed &&
                w.broughtByName &&
                (w.broughtByRevealed !== false || phase === 'final') && (
                  <span className="block text-xs text-cream-300/60">
                    Brought by {w.broughtByName}
                  </span>
                )}
            </div>
            {w.myScore && (
              <span className="text-xs">
                You {w.myScore.total ?? '—'} · Group avg{' '}
                {w.groupAverage != null ? Number(w.groupAverage).toFixed(2) : '—'}
              </span>
            )}
            {showMemberRank && w.myMemberRank != null && (
              <span className="text-xs text-gold-400" title="Your rank for this wine">
                You #{w.myMemberRank}
              </span>
            )}
            <span className="text-sm tabular-nums text-cream-200">
              {points(w.groupScore)}
              <span className="text-cream-300/40"> pts</span>
            </span>
            <span className="w-14 text-right text-xs text-cream-300/40">
              {w.scoreCount ?? 0} {w.scoreCount === 1 ? 'vote' : 'votes'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ScoringGrid({ view, readOnly }: { view: LiveTastingView; readOnly?: boolean }) {
  const [local, setLocal] = useState<Record<string, Components>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const noteTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const qc = useQueryClient();
  const scoreMut = useMutation({
    mutationFn: (body: ScoreBody) => tastingsApi.score(view.eventId, body),
    retry: 2,
  });
  // The tasting's own scale: the default 1–3 / 1–3 / 1–4 unless it says otherwise.
  const scheme = view.scoringScheme ?? DEFAULT_SCORING_SCHEME;

  function current(w: LiveWineView): Components {
    return (
      local[w.id] ?? {
        sightNose: w.myScore?.sightNose ?? null,
        taste: w.myScore?.taste ?? null,
        overall: w.myScore?.overall ?? null,
      }
    );
  }

  function noteFor(w: LiveWineView): string {
    return notes[w.id] ?? w.myScore?.notes ?? '';
  }

  function save(w: LiveWineView, comps: Components, note: string) {
    scoreMut.mutate(
      {
        wineId: w.id,
        clientId: clientIdFor(view.eventId, w.id),
        clientUpdatedAt: new Date().toISOString(),
        notes: note || undefined,
        ...comps,
      },
      { onSuccess: (fresh) => qc.setQueryData(['live', view.eventId], fresh) },
    );
  }

  function pick(w: LiveWineView, field: keyof Components, value: number) {
    if (readOnly) return;
    const next = { ...current(w), [field]: value };
    setLocal((m) => ({ ...m, [w.id]: next }));
    save(w, next, noteFor(w));
  }

  /**
   * Notes save on a debounce — every keystroke would be a write, and the live
   * version counter would make every other member's poller re-fetch.
   */
  function typeNote(w: LiveWineView, text: string) {
    if (readOnly) return;
    setNotes((m) => ({ ...m, [w.id]: text }));
    clearTimeout(noteTimers.current[w.id]);
    noteTimers.current[w.id] = setTimeout(() => save(w, current(w), text), 800);
  }

  return (
    <div className="card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-wide text-gold-400">
          Your scorecard{readOnly ? ' (review)' : ''}
        </h2>
        <span className="text-xs text-cream-300/40">
          {readOnly
            ? 'Scoring closed'
            : scoreMut.isPending
              ? 'Saving…'
              : scoreMut.isError
                ? 'Save failed — tap a score to retry'
                : 'Saved'}
        </span>
      </div>
      <div className="space-y-4">
        {view.wines.map((w) => {
          const c = current(w);
          const total = computeTotal(c);
          return (
            <div key={w.id} className="rounded-lg border border-ink-800 bg-ink-950/60 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="font-display text-2xl text-cream-50">{w.letter}</span>
                <div className="flex items-center gap-3 text-sm text-cream-300/70">
                  {w.myMemberRank != null && (
                    <span className="text-gold-300">Your rank #{w.myMemberRank}</span>
                  )}
                  <span>
                    Total <span className="font-display text-xl text-gold-200">{total ?? '—'}</span>
                    <span className="text-cream-300/40">/{maxTotal(scheme)}</span>
                  </span>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Sight / Nose">
                  <Seg
                    value={c.sightNose}
                    min={scheme.sightNose.min}
                    max={scheme.sightNose.max}
                    label={`${w.letter} sight`}
                    disabled={readOnly}
                    onPick={(n) => pick(w, 'sightNose', n)}
                  />
                </Field>
                <Field label="Taste">
                  <Seg
                    value={c.taste}
                    min={scheme.taste.min}
                    max={scheme.taste.max}
                    label={`${w.letter} taste`}
                    disabled={readOnly}
                    onPick={(n) => pick(w, 'taste', n)}
                  />
                </Field>
                <Field label="Overall">
                  <Seg
                    value={c.overall}
                    min={scheme.overall.min}
                    max={scheme.overall.max}
                    label={`${w.letter} overall`}
                    disabled={readOnly}
                    onPick={(n) => pick(w, 'overall', n)}
                  />
                </Field>
              </div>

              <textarea
                className="field mt-3 min-h-[56px] text-sm"
                placeholder={`Your note on wine ${w.letter} — visible to you and administrators before reveal.`}
                maxLength={2000}
                disabled={readOnly}
                value={noteFor(w)}
                onChange={(e) => typeNote(w, e.target.value)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="mb-1 block text-[11px] uppercase tracking-wide text-cream-300/60">
        {label}
      </span>
      {children}
    </div>
  );
}

function AdminReveal({ view, onChange }: { view: LiveTastingView; onChange: () => void }) {
  const { data: members = [] } = useQuery({
    queryKey: ['members'],
    queryFn: () => membersApi.list(),
  });
  const { data: registrations = [] } = useQuery({
    queryKey: ['registrations', view.eventId],
    queryFn: () => tastingsApi.registrations(view.eventId),
  });
  // Which letter each registration's bottle is under (one letter per bottle).
  const letterByRegistration = new Map(
    view.wines.filter((w) => w.registrationId).map((w) => [w.registrationId!, w.letter]),
  );
  const update = useMutation({
    mutationFn: ({ wineId, patch }: { wineId: string; patch: Record<string, unknown> }) =>
      tastingsApi.updateWine(view.eventId, wineId, patch),
    onSuccess: onChange,
  });

  const tied = tiedRanks(view.wines);

  return (
    <div className="card">
      <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">Reveal wines</h2>
      <div className="space-y-3">
        {/* Worst first — the reveal climbs to the winner. */}
        {[...view.wines]
          .sort(
            (a, b) => (b.groupRank ?? 0) - (a.groupRank ?? 0) || a.letter.localeCompare(b.letter),
          )
          .map((w) => (
            <RevealRow
              eventId={view.eventId}
              phase={view.phase}
              key={w.id}
              wine={w}
              registrations={registrations}
              letterByRegistration={letterByRegistration}
              onChange={onChange}
              rank={
                w.groupRank != null
                  ? `${rankLabel(w.groupRank, tied)} · ${points(w.groupScore)} pts`
                  : 'No scores yet'
              }
              members={members}
              onSave={(patch) => update.mutate({ wineId: w.id, patch })}
            />
          ))}
      </div>
    </div>
  );
}

function RevealRow({
  eventId,
  phase,
  wine,
  rank,
  members,
  registrations,
  letterByRegistration,
  onChange,
  onSave,
}: {
  eventId: string;
  phase: EventPhase;
  wine: LiveWineView;
  /** "7th · 56.5 pts", "tied 7th · …", or "No scores yet". */
  rank: string;
  members: { id: string; firstName: string; lastName: string }[];
  registrations: Registration[];
  letterByRegistration: Map<string, string>;
  onChange: () => void;
  onSave: (patch: Record<string, unknown>) => void;
}) {
  const [wineName, setWineName] = useState(wine.wineName ?? '');
  const [producer, setProducer] = useState(wine.producer ?? '');
  const [vintage, setVintage] = useState(wine.vintage ? String(wine.vintage) : '');
  const [broughtBy, setBroughtBy] = useState(wine.broughtByPersonId ?? '');
  const [broughtByTouched, setBroughtByTouched] = useState(false);

  useEffect(() => {
    setWineName(wine.wineName ?? '');
    setProducer(wine.producer ?? '');
    setVintage(wine.vintage ? String(wine.vintage) : '');
    if (!broughtByTouched) setBroughtBy(wine.broughtByPersonId ?? '');
  }, [wine, broughtByTouched]);

  const linkedLabel = wine.catalogId
    ? `${wine.vintage ? `${wine.vintage} ` : ''}${wine.wineName ?? 'Cellar wine'}`
    : null;

  return (
    <div className="rounded-lg border border-ink-800 bg-ink-950/60 p-3">
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-display text-xl text-cream-50">{wine.letter}</span>
        <span className="text-sm tabular-nums text-gold-200">{rank}</span>
        {wine.revealed && <span className="text-xs text-gold-300">Wine revealed</span>}
        {wine.broughtByRevealed && <span className="text-xs text-gold-300">· Name revealed</span>}
      </div>
      <RevealControls
        eventId={eventId}
        phase={phase}
        wine={wine}
        registrations={registrations}
        letterByRegistration={letterByRegistration}
        onChange={onChange}
      />
      <details className="mt-3">
        <summary className="cursor-pointer text-xs text-cream-300/70 hover:text-gold-200">
          Correct the details
        </summary>
        <div className="mb-2 mt-2">
          <CellarWinePicker
            linkedLabel={linkedLabel}
            onLink={(catalogId) => onSave({ catalogId })}
            onUnlink={() => onSave({ catalogId: null })}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-4">
          <input
            className="field"
            placeholder="Wine name"
            value={wineName}
            onChange={(e) => setWineName(e.target.value)}
          />
          <input
            className="field"
            placeholder="Producer"
            value={producer}
            onChange={(e) => setProducer(e.target.value)}
          />
          <input
            className="field"
            placeholder="Vintage"
            value={vintage}
            onChange={(e) => setVintage(e.target.value)}
          />
          <select
            className="field"
            value={broughtBy}
            onChange={(e) => {
              setBroughtByTouched(true);
              setBroughtBy(e.target.value);
            }}
          >
            <option value="">Brought by…</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            className="btn-ghost px-3 py-1 text-xs"
            onClick={() => {
              const patch: Record<string, unknown> = {
                wineName: wineName || null,
                producer: producer || null,
                vintage: vintage ? Number(vintage) : null,
              };
              if (broughtByTouched) patch.broughtByPersonId = broughtBy || null;
              onSave(patch);
            }}
          >
            Save details
          </button>
        </div>
      </details>
    </div>
  );
}
